import MarketList from '@/components/modules/MarketList';
import { Highlights, MarketInstrument } from '@/components/modules/MarketList/UserMarket';
import { isRoundedPlatform, roundedFullClassname } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import { PairMap } from '@/state/reducers/marketPairSlice';
import useActivePair from '@/utils/hooks/useActivePair';
import { useClickOutside } from '@/utils/hooks/useClickOutside';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import { GetColorIndexesReturn, useInstrument } from '@/utils/hooks/useInstrument';
import usePreviousSelector from '@/utils/hooks/usePreviousSelector';
import cn from 'classnames';
import { Fragment, useMemo, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

export const subHeaderClassname = cn(
    'flex items-center justify-between w-full text-sm p-2 px-1.5 pr-2 cursor-pointer bg-brand-background hover:bg-brand-background-dark',
    { 'mx-3': isRoundedPlatform },
    roundedFullClassname
);

const MobileSubHeader = () => {
    const activePair = useActivePair();
    const marketListDisclosure = useDisclosure(false);
    const ref = useClickOutside(marketListDisclosure[1].close);

    return (
        <div
            ref={ref}
            className={cn(
                'flex lg:hidden w-full border-x-[1.5px] border-brand-border border-b-[1.5px] h-10 text-brand-text',
                roundedFullClassname
            )}>
            <div
                className={subHeaderClassname}
                onClick={() => {
                    if (activePair?.celer) {
                        marketListDisclosure[1].toggle();
                    }
                }}>
                {activePair?.celer && (
                    <Fragment>
                        <div className="flex items-center gap-5">
                            <MarketInstrument market={activePair} isMobileSubHeader />
                            <CelerPrice market={activePair} />
                        </div>
                        {marketListDisclosure[0] ? (
                            <LuChevronLeft className="h-3.5 w-3.5 rotate-90" />
                        ) : (
                            <LuChevronRight className="h-3.5 w-3.5" />
                        )}
                    </Fragment>
                )}
            </div>
            {marketListDisclosure[0] && (
                <div
                    style={{ width: 'calc(100% - 3.5px)' }}
                    className={cn(
                        'pop-up-market-list absolute bottom-[44px] left-[1.5px] z-10 bg-brand-background overflow-clip',
                        roundedFullClassname
                    )}>
                    <MarketList onClick={marketListDisclosure[1].close} />
                </div>
            )}
        </div>
    );
};

const CelerPrice = ({ market }: { market: PairMap }) => {
    const activePair = useActivePair();

    const priceBooksMidPrice = useAppSelector((state) => state.celerMarket.priceBooks[activePair?.celer]?.midprice);
    const prevPriceBooksMidPrice = usePreviousSelector(
        (state) => state.celerMarket.priceBooks[activePair?.celer]?.midprice
    ) as number;

    const [priceHighlights, setPriceHighlights] = useState<GetColorIndexesReturn | undefined>();

    const { formatPip, formatHighlights } = useInstrument(market.celer);

    // currently displaying close
    const midPrice = useMemo(() => priceBooksMidPrice && formatPip(priceBooksMidPrice), [priceBooksMidPrice]);

    // // for highlighting changes based on prev price
    useDidUpdate(() => {
        if (priceBooksMidPrice && prevPriceBooksMidPrice)
            setPriceHighlights(formatHighlights(prevPriceBooksMidPrice, priceBooksMidPrice, priceHighlights));
    }, [priceBooksMidPrice]);

    return (
        <div className="flex items-center gap-2">
            {priceBooksMidPrice > 0 && midPrice && (
                <span>
                    <Highlights ticker={midPrice} highlights={priceHighlights} />
                </span>
            )}
        </div>
    );
};

export default MobileSubHeader;
